<template>
  <div class="flex flex-column">
    <div class="flex align-items-center justify-content-around">
      <div class="flex flex-column">
        <span class="flex-shrink-0 mr-3">Operating Range: </span>
        <PumpFrequencyRange
          v-model.lazy="frequencyRange"
          class="mr-2"
          :motor-poles="[_calcs.motorCalculations.motorPoles.value]"
          @update:model-value="frequencyRangeUpdated"
        />
      </div>
      <div class="flex flex-column justify-content-end">
        <div class="flex flex-column align-items-start gap-1">
          <label>Pump</label>
          <Dropdown
            v-model="selectedPump"
            optionLabel="label"
            :options="pumpOptions"
            append-to="body"
          />
        </div>
      </div>
    </div>
    <TaperedSinglePumpTDHChart
      draw-legend
      :is-horizontal="false"
      :min-frequency="minFrequency"
      :max-frequency="maxFrequency"
      :minimum-height-px="minimumHeightPx"
      :xref-pump="selectedPump?.pump"
      show-corrected-ratio-badge
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import PumpFrequencyRange from "./PumpFrequencyRange.vue";
import { scenarioCalculationsKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import TaperedSinglePumpTDHChart from "@/components/scenarios/charts/TaperedSinglePumpTDHChart.vue";
import { injectStrict } from "@/utils/utils";
import { useSettingsStore } from "@/store";

defineProps<{
  minimumHeightPx: number;
  useEvaluationOverrides?: boolean;
  startingFrequencyRange?: [number, number];
}>();
const emit = defineEmits<{
  (e: "frequencyRangeUpdated", newFrequencyRange: [number, number]): void;
}>();

const _calcs = injectStrict(scenarioCalculationsKey);

const { flags } = useSettingsStore();
const frequencyRange = ref<[number, number]>([
  flags.value.minimumPumpChartFrequency,
  flags.value.maximumPumpChartFrequency
]);
const minFrequency = computed(() => frequencyRange.value[0]);
const maxFrequency = computed(() => frequencyRange.value[1]);
const frequencyRangeUpdated = (val: [number, number]) => {
  emit("frequencyRangeUpdated", val);
};

const pumpOptions = computed(() => [
  ..._calcs.descendingOrderPumps.value.map(p => {
    return {
      label: `${p.r_pump?.series_model}${_calcs.primaryPump.value?.id === p.id ? " (primary)" : ""}`,
      pump: p
    };
  })
]);
const selectedPump = ref(pumpOptions.value[0]);

// Make sure selected pump in dropdown gets set after form/calc changes
watchEffect(() => {
  if (pumpOptions.value.length === 0) return;

  const allPumpLabels = pumpOptions.value.map(({ label }) => label);
  if (!allPumpLabels.includes(selectedPump.value.label)) {
    selectedPump.value = pumpOptions.value[0];
  }
});
</script>

<style lang="scss" scoped>
.top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5em;
}
.two-up {
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
}
.chart-grid {
  padding-bottom: 1em;
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));

  > div {
    box-shadow: var(--shadow-main);
    padding: 1.5em 1.5em 0em 1em;
    background-color: var(--surface-300);
    border-radius: 0.25em;
  }
}
</style>
